'use client';

import React from 'react';

import { Image } from '@/components/atoms';
import { getRandomDefaultImageImport } from '@/lib/utils/defaultImages';

import type { StaticImageData } from 'next/image';

// gap-1 = 4px; account for it when computing per-cell pixel dimensions
const GRID_GAP_PX = 4;

type PlaylistImageProps = {
  images: { src: string; title: string }[];
  size?: number;
  placeholderSrc?: string | StaticImageData;
  /**
   * Responsive mosaic: fills the parent container using CSS grid.
   * Each cell uses `fill` so Next.js optimisation still applies.
   */
  responsive?: boolean;
  /**
   * Single-image mode: renders the first available image (or the
   * placeholder if the array is empty) at the specified `size`.
   */
  singleImage?: boolean;
};

export const PlaylistImage: React.FC<PlaylistImageProps> = ({
  images,
  size = 260,
  placeholderSrc = getRandomDefaultImageImport(),
  responsive = false,
  singleImage = false,
}) => {
  // Build a 4-item array, filling missing slots with random placeholders
  const mosaicImages = Array.from({ length: 4 }, (_, i) => ({
    src: images[i]?.src || getRandomDefaultImageImport(),
    title: images[i]?.title || 'Default playlist image',
  }));

  // ── Single-image mode ────────────────────────────────────────────────────
  if (singleImage) {
    const src = images[0]?.src || placeholderSrc;
    const alt = images[0]?.title || 'Playlist image';
    return (
      <Image
        src={src}
        alt={alt}
        width={size}
        height={size}
        aspectRatio="square"
        className="object-cover"
      />
    );
  }

  // ── Responsive mosaic ────────────────────────────────────────────────────
  // Fills the parent; each cell is a relative container so `fill` works.
  if (responsive) {
    return (
      <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-1">
        {mosaicImages.map((image, index) => (
          <div key={index} className="relative overflow-hidden">
            <Image
              src={image.src}
              alt={image.title}
              fill
              className="object-cover"
            />
          </div>
        ))}
      </div>
    );
  }

  // ── Fixed mosaic ─────────────────────────────────────────────────────────
  // Explicit pixel dimensions; subtract the gap so cells fit the container exactly.
  const cellSize = (size - GRID_GAP_PX) / 2;
  return (
    <div
      className="grid grid-cols-2 grid-rows-2 gap-1"
      style={{ width: size, height: size }}
    >
      {mosaicImages.map((image, index) => (
        <Image
          key={index}
          src={image.src}
          alt={image.title}
          width={cellSize}
          height={cellSize}
          aspectRatio="square"
          className="object-cover"
        />
      ))}
    </div>
  );
};
